import React, { useEffect, useMemo, useState } from "react";
import api from "../axiosSetup";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import SRVOfferDetailModal from "./SRVOfferDetailModal";
import "../css/marketplace-dark.css";
import cfg from "../../config";

const LS_HIDDEN_OFFERS = "swap_hidden_offers";
function loadHiddenOffers() {
  try { return new Set(JSON.parse(localStorage.getItem(LS_HIDDEN_OFFERS) || "[]")); }
  catch { return new Set(); }
}
function saveHiddenOffers(set) {
  localStorage.setItem(LS_HIDDEN_OFFERS, JSON.stringify([...set]));
}

const STATUS_OPTIONS = [
  { value: "", label: "Alle Status" },
  { value: "ausstehend", label: "Ausstehend" },
  { value: "angenommen_durch_eigentuemer", label: "Angenommen" },
  { value: "getauscht", label: "Getauscht" },
  { value: "abgelehnt", label: "Abgelehnt" },
  { value: "zurueckgezogen", label: "Zurückgezogen" },
  { value: "abgelaufen", label: "Abgelaufen" }
];

export default function SRVMyOffersCards() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("all"); // all | incoming | outgoing
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const [openDetail, setOpenDetail] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState(null);

  const [hiddenOfferIds, setHiddenOfferIds] = useState(loadHiddenOffers);
  const [showHidden, setShowHidden] = useState(false);

  const hideOffer = (id) => {
    setHiddenOfferIds(prev => {
      const next = new Set(prev);
      next.add(id);
      saveHiddenOffers(next);
      return next;
    });
  };

  const unhideAll = () => {
    setHiddenOfferIds(new Set());
    saveHiddenOffers(new Set());
    setShowHidden(false);
  };

  const token = localStorage.getItem("authToken");

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (type)   params.set("type", type);
      if (status) params.set("status", status);
      if (q?.trim()) params.set("q", q.trim());

      const res = await api.get(`/api/swap-marketplace/my-offers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Fehler beim Laden der Gegenangebote.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hiddenCount = useMemo(() => items.filter(it => hiddenOfferIds.has(it.offerId)).length, [items, hiddenOfferIds]);
  const list = useMemo(
    () => showHidden ? items : items.filter(it => !hiddenOfferIds.has(it.offerId)),
    [items, hiddenOfferIds, showHidden]
  );

  const roleBadge = (role) =>
    role === "incoming"
      ? <span className="badge bg-success">Eingegangen</span>
      : <span className="badge bg-primary">Gesendet</span>;

  const statusBadge = (s) => {
    const map = {
      ausstehend: { cls: "bg-warning text-dark", text: "Ausstehend" },
      angenommen_durch_eigentuemer: { cls: "bg-success", text: "Angenommen" },
      abgelehnt: { cls: "bg-danger", text: "Abgelehnt" },
      zurueckgezogen: { cls: "bg-secondary", text: "Zurückgezogen" },
      abgelaufen: { cls: "bg-dark", text: "Abgelaufen" }
    };
    const hit = map[s] || { cls: "bg-secondary", text: s || "—" };
    return <span className={`badge ${hit.cls}`}>{hit.text}</span>;
  };

  // Optionales Council-Badge (falls Backend councilStatus liefert)
  const councilBadge = (cs) => {
    if (!cs) return null;
    const map = {
      ausstehend: { cls: "bg-info text-dark", text: `${cfg.adminRoleName}: ausstehend` },
      genehmigt:  { cls: "bg-success",       text: `${cfg.adminRoleName}: genehmigt` },
      abgelehnt:  { cls: "bg-danger",        text: `${cfg.adminRoleName}: abgelehnt` }
    };
    const hit = map[cs] || { cls: "bg-secondary", text: `${cfg.adminRoleName}: ${cs}` };
    return <span className={`badge ${hit.cls}`}>{hit.text}</span>;
  };

  const snippet = (txt = "", limit = 240) =>
    txt.length > limit ? `${txt.slice(0, limit)}…` : txt;

  return (
    <div className="container my-3 marketplace-dark">
      {/* Filterleiste */}
      <div className="d-flex flex-column flex-lg-row align-items-lg-end gap-2 mb-3">
        <div className="btn-group" role="group" aria-label="Typfilter">
          <button
            type="button"
            className={`btn btn-sm ${type === "all" ? "btn-light" : "btn-outline-light"}`}
            onClick={() => setType("all")}
          >
            Alle
          </button>
          <button
            type="button"
            className={`btn btn-sm ${type === "incoming" ? "btn-light" : "btn-outline-light"}`}
            onClick={() => setType("incoming")}
          >
            Eingegangen
          </button>
          <button
            type="button"
            className={`btn btn-sm ${type === "outgoing" ? "btn-light" : "btn-outline-light"}`}
            onClick={() => setType("outgoing")}
          >
            Gesendet
          </button>
        </div>

        <div className="flex-grow-1">
          <label className="form-label">Suche</label>
          <input
            className="form-control"
            placeholder="Nach Einsatz, Person oder Nachricht suchen…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchData()}
          />
        </div>

        <div style={{ minWidth: 220 }}>
          <label className="form-label">Status</label>
          <select
            className="form-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <button className="btn btn-primary" onClick={fetchData}>
            Aktualisieren
          </button>
        </div>
      </div>

      {/* Liste / Empty State */}
      {loading ? (
        <p>Lade Gegenangebote…</p>
      ) : list.length === 0 ? (
        <div
          className="alert"
          style={{
            backgroundColor: "rgba(255,255,255,0.08)",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.25)"
          }}
        >
          Es liegen aktuell keine passenden Gegenangebote vor.
        </div>
      ) : (
        <div className="row g-3">
          {list.map((it) => (
            <div className="col-12" key={it.offerId}>
              <div
                className="card bg-transparent border-1"
                style={{ borderColor: "rgba(255,255,255,0.25)" }}
              >
                <div className="card-body text-white">
                  {/* Kopf: Rolle + Status + Datum + (optional) Council */}
                  <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      {roleBadge(it.role)}
                      {statusBadge(it.status)}
                      {/* zeigt Badge nur, wenn councilStatus vorhanden ist */}
                      {councilBadge?.(it.councilStatus)}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-white-50" style={{ fontSize: 13 }}>
                        erstellt: {dayjs(it.createdAt).format("DD.MM.YYYY HH:mm")}
                      </span>
                      <button
                        title="Ausblenden"
                        aria-label="Gegenangebot ausblenden"
                        onClick={() => hideOffer(it.offerId)}
                        style={{
                          background: "none", border: "1px solid rgba(255,255,255,0.2)",
                          color: "rgba(255,255,255,0.4)", borderRadius: 6,
                          padding: "2px 7px", fontSize: 12, cursor: "pointer",
                          lineHeight: 1.4,
                        }}
                      >
                        ✕ Ausblenden
                      </button>
                    </div>
                  </div>

                  {/* Partner & Kurzinformation */}
                  <div className="mt-2">
                    <div className="text-white-50">
                      {it.role === "incoming" ? "Von" : "An"}:{" "}
                      <strong className="text-white">{it.otherParty?.name}</strong>
                    </div>
                  </div>

                  {/* Einsätze */}
                  <div className="row g-3 mt-2">
                    <div className="col-12 col-md-6">
                      <div className="fw-semibold">Angebotener Einsatz</div>
                      <div>{it.offeredAssignment.name}</div>
                      {Array.isArray(it.offeredAssignment.dates) && it.offeredAssignment.dates.length > 0 ? (
                        <ul className="m-0 ps-3">
                          {it.offeredAssignment.dates.slice(0, 3).map((d, i) => (
                            <li key={i}>
                              {dayjs(d.workDate).format("DD.MM.YYYY")} —{" "}
                              {String(d.timeFrom).slice(0, 5)}–{String(d.timeTo).slice(0, 5)} Uhr
                            </li>
                          ))}
                          {it.offeredAssignment.dates.length > 3 && (
                            <li className="text-white-50">
                              … {it.offeredAssignment.dates.length - 3} weitere Termin(e)
                            </li>
                          )}
                        </ul>
                      ) : (
                        <div className="text-white-50">Keine Terminangaben verfügbar.</div>
                      )}

                        {/* ➕ Nachricht des Listings */}
                        {(it.listing?.message || it.listing?.reason) && (
                            <div className="mt-2">
                            <div className="fw-semibold">Nachricht des Angebots:</div>
                            <div className="text-white-75" style={{ whiteSpace: "pre-wrap" }}>
                                {snippet(it.listing.message || it.listing.reason)}
                            </div>
                            </div>
                        )}
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="fw-semibold">ursprünglicher Einsatz:</div>
                      <div>{it.listing.assignmentName}</div>
                      {Array.isArray(it.listing.dates) && it.listing.dates.length > 0 ? (
                        <ul className="m-0 ps-3">
                          {it.listing.dates.slice(0, 3).map((d, i) => (
                            <li key={i}>
                              {dayjs(d.workDate).format("DD.MM.YYYY")} —{" "}
                              {String(d.timeFrom).slice(0, 5)}–{String(d.timeTo).slice(0, 5)} Uhr
                            </li>
                          ))}
                          {it.listing.dates.length > 3 && (
                            <li className="text-white-50">
                              … {it.listing.dates.length - 3} weitere Termin(e)
                            </li>
                          )}
                        </ul>
                      ) : (
                        <div className="text-white-50">Keine Terminangaben verfügbar.</div>
                      )}
                    </div>
                  </div>

                  {/* Nachricht (Gegenangebot) */}
                  {it.message && (
                    <div className="mt-2">
                      <div className="fw-semibold">Nachricht des Gegenangebots:</div>
                      <div className="text-white-75" style={{ whiteSpace: "pre-wrap" }}>
                        {snippet(it.message)}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-3 d-flex justify-content-end">
                    <button
                      className="btn btn-outline-light btn-sm"
                      onClick={() => {
                        setSelectedOfferId(it.offerId);
                        setOpenDetail(true);
                      }}
                    >
                      Details ansehen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Ausgeblendete-Bar */}
          {hiddenCount > 0 && (
            <div className="col-12">
              <div
                style={{
                  padding: "10px 14px", borderRadius: 10, fontSize: 13,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.04)",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  color: "rgba(255,255,255,0.5)",
                }}
              >
                <span>{hiddenCount} Gegenangebot{hiddenCount !== 1 ? "e" : ""} ausgeblendet</span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    style={{ fontSize: 12, padding: "2px 10px" }}
                    onClick={() => setShowHidden(v => !v)}
                  >
                    {showHidden ? "Wieder verstecken" : "Einblenden"}
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    style={{ fontSize: 12, padding: "2px 10px" }}
                    onClick={unhideAll}
                  >
                    Alle zurücksetzen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Detail-Modal */}
          {openDetail && selectedOfferId != null && selectedOfferId !== 0 && (
            <SRVOfferDetailModal
              open={openDetail}
              onClose={() => {
                setOpenDetail(false);
                setSelectedOfferId(null);
              }}
              offerId={selectedOfferId}
              onAfterAction={() => {
                setOpenDetail(false);
                setSelectedOfferId(null);
                fetchData(); // Liste refreshen, falls Status geändert wurde
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}